/**
 * ScenarioEditor Styles
 *
 * MUI styling objects for the ScenarioEditor component.
 * Includes both standard Vortx styles and retro mode cascading styles.
 */

import { alpha } from '@mui/material/styles';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

/**
 * Standard Vortx styling for cards, text fields, and buttons
 */
export const vortxStyles = {
    card: {
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.2s',
        '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: vortxColors.gold,
            },
            '&.Mui-focused fieldset': {
                borderColor: vortxColors.gold,
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: vortxColors.gold,
        },
    },
    button: {
        bgcolor: vortxColors.gold,
        color: 'white',
        '&:hover': {
            bgcolor: alpha(vortxColors.gold, 0.9),
        },
    },
};

/**
 * Retro mode cascading styles - applies to all nested MUI components
 * Used when isRetro theme mode is active
 */
export const retroContainerStyles = {
    // Cards
    '& .MuiCard-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: 0,
        border: '1px solid rgba(63, 106, 100, 0.4)',
        boxShadow: 'none',
    },
    '& .MuiCardContent-root': {
        backgroundColor: 'transparent',
    },
    // TextFields
    '& .MuiTextField-root, & .MuiFormControl-root': {
        '& .MuiOutlinedInput-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.85rem',
            color: '#c8c8c8',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            borderRadius: 0,
            '& fieldset': {
                borderColor: 'rgba(63, 106, 100, 0.4)',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(95, 212, 196, 0.6)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#5fd4c4',
                borderWidth: '1px',
                boxShadow: '0 0 8px rgba(95, 212, 196, 0.25)',
            },
        },
        '& .MuiInputLabel-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            '&.Mui-focused': {
                color: '#5fd4c4',
            },
        },
        '& .MuiInputBase-input': {
            color: '#c8c8c8',
        },
        '& .MuiInputAdornment-root': {
            color: '#888',
            '& .MuiTypography-root': {
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
            },
        },
        '& .MuiFormHelperText-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.65rem',
            color: '#888',
        },
    },
    // Select menus
    '& .MuiSelect-icon': {
        color: '#888',
    },
    // Checkboxes and Switches
    '& .MuiCheckbox-root': {
        color: 'rgba(63, 106, 100, 0.6)',
        '&.Mui-checked': {
            color: '#5fd4c4',
        },
    },
    '& .MuiSwitch-root': {
        '& .MuiSwitch-switchBase': {
            color: '#888',
            '&.Mui-checked': {
                color: '#5fd4c4',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#5fd4c4',
                    opacity: 0.5,
                },
            },
        },
        '& .MuiSwitch-track': {
            backgroundColor: 'rgba(63, 106, 100, 0.4)',
        },
    },
    '& .MuiFormControlLabel-label': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.8rem',
        color: '#c8c8c8',
    },
    // Buttons
    '& .MuiButton-outlined': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        borderRadius: 0,
        borderWidth: '1px',
        '&:hover': {
            borderWidth: '1px',
        },
    },
    '& .MuiButton-contained': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        borderRadius: 0,
        boxShadow: 'none',
        '&:hover': {
            boxShadow: `0 0 15px rgba(178, 140, 80, 0.4)`,
        },
    },
    '& .MuiIconButton-root': {
        borderRadius: 0,
    },
    // ToggleButtonGroup
    '& .MuiToggleButtonGroup-root .MuiToggleButton-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#888',
        borderColor: 'rgba(63, 106, 100, 0.4)',
        borderRadius: 0,
        '&:hover': {
            backgroundColor: 'rgba(95, 212, 196, 0.1)',
        },
        '&.Mui-selected': {
            backgroundColor: 'rgba(95, 212, 196, 0.2)',
            color: '#5fd4c4',
            borderColor: '#5fd4c4',
            '&:hover': {
                backgroundColor: 'rgba(95, 212, 196, 0.25)',
            },
        },
    },
    // Alerts
    '& .MuiAlert-root': {
        fontFamily: vortxTypography.mono,
        borderRadius: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(63, 106, 100, 0.4)',
        '& .MuiAlert-message': {
            color: '#c8c8c8',
        },
    },
    '& .MuiAlert-standardWarning': {
        borderColor: '#f5a623',
        backgroundColor: 'rgba(245, 166, 35, 0.1)',
        '& .MuiAlert-icon': {
            color: '#f5a623',
        },
    },
    '& .MuiAlert-standardError': {
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        '& .MuiAlert-icon': {
            color: '#e74c3c',
        },
    },
    '& .MuiAlert-standardSuccess': {
        borderColor: '#00ff41',
        backgroundColor: 'rgba(0, 255, 65, 0.08)',
        '& .MuiAlert-icon': {
            color: '#00ff41',
        },
    },
    '& .MuiAlert-standardInfo': {
        borderColor: '#5fd4c4',
        backgroundColor: 'rgba(95, 212, 196, 0.1)',
        '& .MuiAlert-icon': {
            color: '#5fd4c4',
        },
    },
    // Chips
    '& .MuiChip-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.7rem',
        borderRadius: 0,
        height: 'auto',
        padding: '4px 8px',
    },
    // Dividers
    '& .MuiDivider-root': {
        borderColor: 'rgba(63, 106, 100, 0.3)',
    },
    // Typography in cards/sections
    '& .MuiTypography-h6, & .MuiTypography-subtitle1': {
        fontFamily: vortxTypography.heading,
    },
    '& .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-caption': {
        fontFamily: vortxTypography.mono,
    },
    // Tables
    '& .MuiTable-root': {
        '& .MuiTableCell-root': {
            fontFamily: vortxTypography.mono,
            borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
            color: '#c8c8c8',
            fontSize: '0.8rem',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#b28c50',
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(95, 212, 196, 0.08)',
        },
    },
    // Dialogs
    '& .MuiDialog-paper': {
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(63, 106, 100, 0.4)',
        borderRadius: 0,
    },
    '& .MuiDialogTitle-root': {
        fontFamily: vortxTypography.heading,
        color: '#b28c50',
        borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
    },
    '& .MuiDialogContent-root': {
        backgroundColor: 'transparent',
    },
    '& .MuiDialogActions-root': {
        borderTop: '1px solid rgba(63, 106, 100, 0.3)',
    },
    // DatePickers
    '& .MuiPickersDay-root': {
        fontFamily: vortxTypography.mono,
    },
    // Linear Progress
    '& .MuiLinearProgress-root': {
        backgroundColor: 'rgba(63, 106, 100, 0.3)',
        borderRadius: 0,
        height: 4,
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#5fd4c4',
        },
    },
    // Circular Progress
    '& .MuiCircularProgress-root': {
        color: '#5fd4c4',
    },
};
